import React, { useRef, useState, useEffect, useCallback } from 'react';
import { RotateCw, Copy, Trash2, LayoutTemplate, Eraser } from 'lucide-react';

const MARGIN = 150; // cm de marge autour de l'emprise (espace annotations)
const GRID = 50; // cm
const SNAP = 5; // cm

// Rendu label rotation : jamais à l'envers
function labelTransform(r) {
  if (r === 90) return 'rotate(90deg)';
  if (r === 270) return 'rotate(270deg)';
  if (r === 180) return 'rotate(0deg)';
  return 'none';
}

export function CroquisEditor({ items, L, P, catalogue, selectedId, onChange, onSelect, onLoadModel, onClear }) {
  const planRef = useRef(null);
  const [scale, setScale] = useState(1.4);
  const [zoom, setZoom] = useState(1);
  const dragRef = useRef(null);

  const totalW = L + 2 * MARGIN;
  const totalH = P + 2 * MARGIN;

  // Ajuste l'échelle à la largeur disponible
  useEffect(() => {
    const el = planRef.current?.parentElement;
    if (!el) return;
    const compute = () => {
      const avail = el.clientWidth - 8;
      setScale(Math.max(0.4, Math.min(2.2, avail / totalW)));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [totalW]);

  const catByK = useCallback((k) => catalogue.find((c) => c.k === k) || {}, [catalogue]);

  const clampX = (x, w) => Math.max(-MARGIN, Math.min(L + MARGIN - (w || 0), x));
  const clampY = (y, h) => Math.max(-MARGIN, Math.min(P + MARGIN - (h || 0), y));

  const dims = (it) => {
    const c = catByK(it.k);
    let w = it.w || c.L || 40;
    let h = c.P || (c.type === 'point' || c.type === 'repere' ? 0 : 40);
    if (c.type === 'fleche') { w = it.w || c.L || 140; h = 0; }
    // Formes libres : profondeur réglable (it.p) ; rond → diamètre (h = w)
    if (c.free) { h = it.p || c.P || 40; if (c.shape === 'circle') h = w; }
    // Escalier arrondi (quart de cercle) : jambes égales → carré (h = w)
    if (c.shape === 'quarter') { h = w; }
    // rotation 90/270 : on échange
    if (it.r === 90 || it.r === 270) { const t = w; w = h; h = t; }
    return { w, h };
  };

  const startDrag = (e, it) => {
    e.stopPropagation();
    onSelect(it.id);
    const c = catByK(it.k);
    const point = c.type === 'point' || c.type === 'repere' || c.type === 'fleche';
    const rect = planRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    const startXcm = (cx - rect.left) / scale - MARGIN;
    const startYcm = (cy - rect.top) / scale - MARGIN;
    dragRef.current = { id: it.id, offX: startXcm - it.x, offY: startYcm - it.y, point };
  };

  const onMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const rect = planRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    let xcm = (cx - rect.left) / scale - MARGIN - d.offX;
    let ycm = (cy - rect.top) / scale - MARGIN - d.offY;
    xcm = Math.round(xcm / SNAP) * SNAP;
    ycm = Math.round(ycm / SNAP) * SNAP;
    onChange((prev) => prev.map((p) => {
      if (p.id !== d.id) return p;
      const { w, h } = dims(p);
      return { ...p, x: d.point ? Math.max(-MARGIN, Math.min(L + MARGIN, xcm)) : clampX(xcm, w), y: d.point ? Math.max(-MARGIN, Math.min(P + MARGIN, ycm)) : clampY(ycm, h) };
    }));
  }, [scale, L, P, onChange]);

  const endDrag = useCallback(() => { dragRef.current = null; }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', endDrag);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', endDrag);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', endDrag);
    };
  }, [onMove, endDrag]);

  // Clavier : R pivoter, Suppr retirer, flèches déplacer 5 cm
  useEffect(() => {
    const onKey = (e) => {
      if (!selectedId) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === 'r' || e.key === 'R') { e.preventDefault(); rotate(selectedId); }
      else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); remove(selectedId); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(selectedId, -SNAP, 0); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); nudge(selectedId, SNAP, 0); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); nudge(selectedId, 0, -SNAP); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); nudge(selectedId, 0, SNAP); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, items]);

  const rotate = (id) => onChange((prev) => prev.map((p) => {
    if (p.id !== id) return p;
    const c = catByK(p.k);
    if (c.type === 'point' || c.type === 'repere') return p; // ne pivotent pas
    const step = c.type === 'fleche' ? 45 : 90;
    return { ...p, r: ((p.r || 0) + step) % 360 };
  }));

  const remove = (id) => { onChange((prev) => prev.filter((p) => p.id !== id)); onSelect(null); };

  const duplicate = (id) => onChange((prev) => {
    const it = prev.find((p) => p.id === id);
    if (!it) return prev;
    return [...prev, { ...it, id: 'it_' + Math.random().toString(36).slice(2, 8), x: it.x + 20, y: it.y + 20 }];
  });

  const nudge = (id, dx, dy) => onChange((prev) => prev.map((p) => p.id === id ? { ...p, x: p.x + dx, y: p.y + dy } : p));

  const addItem = (k) => {
    const c = catByK(k);
    const w = c.L || 40; const h = c.P || 40;
    const it = { id: 'it_' + Math.random().toString(36).slice(2, 8), k, x: Math.round((L / 2 - w / 2) / SNAP) * SNAP, y: Math.round((P / 2 - h / 2) / SNAP) * SNAP, r: 0 };
    if (c.sur_mesure && c.L) it.w = c.L;
    if (c.free) { it.p = c.P; it.txt = ''; }
    if (c.type === 'repere') it.txt = '';
    onChange((prev) => [...prev, it]);
    onSelect(it.id);
  };

  const families = [...new Set(catalogue.map((c) => c.famille))];
  let repIndex = 0;

  const px = (cm) => cm * scale * zoom;

  // Détection de chevauchement des meubles/PLV (tolérance 1 cm)
  const overlapIds = React.useMemo(() => {
    const rects = items
      .filter((it) => { const c = catByK(it.k); return c.type && !['point', 'repere', 'fleche'].includes(c.type) && !c.free; })
      .map((it) => { const { w, h } = dims(it); return { id: it.id, x: it.x, y: it.y, w, h }; });
    const s = new Set();
    for (let i = 0; i < rects.length; i++) {
      for (let j = i + 1; j < rects.length; j++) {
        const a = rects[i], b = rects[j];
        if (a.x + 1 < b.x + b.w && a.x + a.w > b.x + 1 && a.y + 1 < b.y + b.h && a.y + a.h > b.y + 1) {
          s.add(a.id); s.add(b.id);
        }
      }
    }
    return s;
    // eslint-disable-next-line
  }, [items, catalogue]);

  return (
    <div>
      {/* Catalogue */}
      <div className="space-y-2 mb-3" data-testid="croquis-catalogue">
        {families.map((fam) => {
          const list = catalogue.filter((c) => c.famille === fam);
          const color = list[0]?.color || '#888';
          return (
            <div key={fam} className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wide font-semibold mr-1" style={{ color: '#6b665d', minWidth: 92 }}>{fam}</span>
              {list.map((c) => (
                <button key={c.k} onClick={() => addItem(c.k)} data-testid={`croquis-add-${c.k}`}
                  className="text-[11px] font-medium px-2 py-1 rounded-md border transition-colors hover:brightness-95"
                  style={{ borderColor: color + '88', color: '#1f1d1a', background: color + '1f' }}
                  title={c.libelle + (c.L ? ` · ${c.L}${c.P ? '×' + c.P : ''}` : '')}>
                  {c.libelle}
                </button>
              ))}
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 mb-2">
        <button onClick={onLoadModel} data-testid="croquis-load-model" className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-orange-500 border border-orange-500 text-white hover:bg-orange-600">
          <LayoutTemplate className="w-3.5 h-3.5" /> Charger le modèle 4 faces {L} × {P}
        </button>
        <button onClick={onClear} data-testid="croquis-clear" className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white border border-stone-300 text-stone-700 hover:bg-stone-100">
          <Eraser className="w-3.5 h-3.5" /> Vider le plan
        </button>
        <div className="ml-auto inline-flex items-center gap-1">
          <button onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.2).toFixed(2)))} data-testid="croquis-zoom-out" className="text-xs px-2 py-1.5 rounded-md bg-white border border-stone-300 text-stone-700 hover:bg-stone-100">−</button>
          <span className="text-[11px] w-10 text-center" style={{ color: '#4a463f' }}>{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom((z) => Math.min(2.6, +(z + 0.2).toFixed(2)))} data-testid="croquis-zoom-in" className="text-xs px-2 py-1.5 rounded-md bg-white border border-stone-300 text-stone-700 hover:bg-stone-100">+</button>
          <button onClick={() => setZoom(1)} data-testid="croquis-zoom-fit" className="text-xs px-2 py-1.5 rounded-md bg-white border border-stone-300 text-stone-700 hover:bg-stone-100">Ajuster</button>
        </div>
      </div>

      {overlapIds.size > 0 && (
        <div className="text-xs px-3 py-1.5 mb-2 rounded-md" style={{ background: 'rgba(225,29,72,.15)', border: '1px solid rgba(225,29,72,.4)', color: '#fca5a5' }} data-testid="croquis-overlap-warning">
          ⚠ {overlapIds.size} meuble(s) se chevauchent — repositionnez les éléments encadrés en rouge.
        </div>
      )}

      {/* Plan */}
      <div className="w-full overflow-auto rounded-lg" style={{ background: '#e9e9e9', padding: 4 }}>
        <div
          ref={planRef}
          onMouseDown={() => onSelect(null)}
          style={{ position: 'relative', width: px(totalW), height: px(totalH), background: '#d9d9d9',
            backgroundImage: 'linear-gradient(#00000010 1px, transparent 1px), linear-gradient(90deg, #00000010 1px, transparent 1px)',
            backgroundSize: `${px(GRID)}px ${px(GRID)}px`, margin: '0 auto' }}
          data-testid="croquis-plan"
        >
          {/* Emprise blanche */}
          <div style={{ position: 'absolute', left: px(MARGIN), top: px(MARGIN), width: px(L), height: px(P),
            background: '#ffffff', border: '2px dashed #111', boxSizing: 'border-box',
            backgroundImage: 'linear-gradient(#00000010 1px, transparent 1px), linear-gradient(90deg, #00000010 1px, transparent 1px)',
            backgroundSize: `${px(GRID)}px ${px(GRID)}px` }} />
          {/* Cotes largeur & profondeur : lignes de cote avec flèches, à 2 carreaux (100 cm) à l'extérieur */}
          {(() => {
            const off = px(2 * GRID);        // 2 carreaux = 100 cm hors emprise
            const x0 = px(MARGIN), x1 = px(MARGIN + L);
            const y0 = px(MARGIN), y1 = px(MARGIN + P);
            const topY = y0 - off;           // ligne de cote horizontale (largeur)
            const leftX = x0 - off;          // ligne de cote verticale (profondeur)
            const col = '#c0392b';
            return (
              <>
              <svg width={px(totalW)} height={px(totalH)} style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', overflow: 'visible' }} data-testid="croquis-cotes">
                <defs>
                  <marker id="cdimArrow" markerWidth="12" markerHeight="12" refX="9" refY="4" orient="auto-start-reverse" markerUnits="userSpaceOnUse">
                    <path d="M0,0 L9,4 L0,8 z" fill={col} />
                  </marker>
                </defs>
                {/* Largeur (haut) */}
                <line x1={x0} y1={y0} x2={x0} y2={topY - 4} stroke={col} strokeWidth="1" strokeDasharray="3 3" opacity="0.7" />
                <line x1={x1} y1={y0} x2={x1} y2={topY - 4} stroke={col} strokeWidth="1" strokeDasharray="3 3" opacity="0.7" />
                <line x1={x0} y1={topY} x2={x1} y2={topY} stroke={col} strokeWidth="1.5" markerStart="url(#cdimArrow)" markerEnd="url(#cdimArrow)" />
                {/* Profondeur (gauche) */}
                <line x1={x0} y1={y0} x2={leftX - 4} y2={y0} stroke={col} strokeWidth="1" strokeDasharray="3 3" opacity="0.7" />
                <line x1={x0} y1={y1} x2={leftX - 4} y2={y1} stroke={col} strokeWidth="1" strokeDasharray="3 3" opacity="0.7" />
                <line x1={leftX} y1={y0} x2={leftX} y2={y1} stroke={col} strokeWidth="1.5" markerStart="url(#cdimArrow)" markerEnd="url(#cdimArrow)" />
              </svg>
              {/* Libellés de cote en HTML (capturés par html2canvas, contrairement au texte SVG) */}
              <div style={{ position: 'absolute', left: x0, top: topY - 20, width: x1 - x0, textAlign: 'center', color: col, fontSize: 13, fontWeight: 700, pointerEvents: 'none' }}>{`Largeur ${L} cm`}</div>
              <div style={{ position: 'absolute', left: leftX - 70 - 12, top: (y0 + y1) / 2 - 10, width: 140, height: 20, lineHeight: '20px', whiteSpace: 'nowrap', textAlign: 'center', color: col, fontSize: 13, fontWeight: 700, transform: 'rotate(-90deg)', pointerEvents: 'none' }}>{`Profondeur ${P} cm`}</div>
              </>
            );
          })()}

          {/* Items */}
          {items.map((it) => {
            const c = catByK(it.k);
            const isSel = it.id === selectedId;
            if (c.type === 'point') {
              return (
                <div key={it.id} onMouseDown={(e) => startDrag(e, it)} onTouchStart={(e) => startDrag(e, it)}
                  data-testid={`croquis-item-${it.id}`}
                  style={{ position: 'absolute', left: px(it.x + MARGIN) - 14, top: px(it.y + MARGIN) - 14, width: 28, height: 28, borderRadius: '50%', background: c.color, color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab', border: isSel ? '2px dashed #111' : '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,.4)' }}>
                  {c.pastille}
                </div>
              );
            }
            if (c.type === 'repere') {
              repIndex += 1;
              return (
                <div key={it.id} onMouseDown={(e) => startDrag(e, it)} onTouchStart={(e) => startDrag(e, it)}
                  data-testid={`croquis-item-${it.id}`}
                  style={{ position: 'absolute', left: px(it.x + MARGIN) - 13, top: px(it.y + MARGIN) - 13, width: 26, height: 26, borderRadius: '50% 50% 50% 0', transform: 'rotate(45deg)', background: '#D9622B', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab', border: isSel ? '2px dashed #111' : '2px solid #fff' }}>
                  <span style={{ transform: 'rotate(-45deg)', fontSize: 11, fontWeight: 700 }}>{repIndex}</span>
                </div>
              );
            }
            if (c.type === 'fleche') {
              const len = it.w || c.L || 140;
              return (
                <div key={it.id} onMouseDown={(e) => startDrag(e, it)} onTouchStart={(e) => startDrag(e, it)}
                  data-testid={`croquis-item-${it.id}`}
                  style={{ position: 'absolute', left: px(it.x + MARGIN) - px(len) / 2, top: px(it.y + MARGIN) - 10, width: px(len), height: 20, transform: `rotate(${it.r || 0}deg)`, transformOrigin: 'center', cursor: 'grab', display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, borderTop: '3px dashed #6E8B2F' }} />
                  <div style={{ width: 0, height: 0, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderLeft: '12px solid #6E8B2F' }} />
                  <span style={{ position: 'absolute', top: -14, left: 0, fontSize: 9, color: '#4a6020', fontWeight: 700 }}>Flux clients</span>
                </div>
              );
            }
            // Meuble / PLV
            const { w, h } = dims(it);
            const r = it.r || 0;
            const frontSide = { 0: 'bottom', 90: 'left', 180: 'top', 270: 'right' }[r] || 'bottom';
            const isPlv = c.type === 'plv';
            const shape = c.shape;
            const quarterRadius = { 0: '100% 0 0 0', 90: '0 100% 0 0', 180: '0 0 100% 0', 270: '0 0 0 100%' }[r] || '100% 0 0 0';
            const radius = shape === 'circle' ? '50%' : shape === 'quarter' ? quarterRadius : shape === 'round-front' ? '8px 8px 2px 2px' : 3;

            // Porte : symbole "portes saloon" — deux vantaux (traits) avec un jeu au centre
            if (shape === 'door') {
              return (
                <div key={it.id} onMouseDown={(e) => startDrag(e, it)} onTouchStart={(e) => startDrag(e, it)}
                  data-testid={`croquis-item-${it.id}`}
                  style={{ position: 'absolute', left: px(it.x + MARGIN), top: px(it.y + MARGIN), width: px(w), height: px(h), cursor: 'grab', boxSizing: 'border-box' }}>
                  <div style={{ position: 'absolute', inset: 0, transform: `rotate(${r}deg)`, transformOrigin: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '18%' }}>
                    {/* vantail gauche */}
                    <div style={{ width: '41%', height: 4, background: c.color, borderRadius: 1 }} />
                    {/* vantail droit */}
                    <div style={{ width: '41%', height: 4, background: c.color, borderRadius: 1 }} />
                  </div>
                  <div style={{ position: 'absolute', inset: 0, border: isSel ? '2px dashed #111' : 'none', boxSizing: 'border-box' }} />
                  <span style={{ position: 'absolute', left: 2, top: 2, fontSize: 9, color: c.color, fontWeight: 700 }}>Porte</span>
                </div>
              );
            }
            return (
              <div key={it.id} onMouseDown={(e) => startDrag(e, it)} onTouchStart={(e) => startDrag(e, it)}
                data-testid={`croquis-item-${it.id}`}
                style={{ position: 'absolute', left: px(it.x + MARGIN), top: px(it.y + MARGIN), width: px(w), height: px(h),
                  background: c.color + (isPlv ? '22' : '55'),
                  border: isSel ? '2px dashed #111' : overlapIds.has(it.id) ? '2px solid #e11d48' : isPlv ? `2px dashed ${c.color}` : `1px solid ${c.color}`,
                  boxShadow: overlapIds.has(it.id) && !isSel ? '0 0 0 2px rgba(225,29,72,.25)' : 'none',
                  borderRadius: radius,
                  boxSizing: 'border-box', cursor: 'grab', overflow: 'visible', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* face avant orange (sauf quart de cercle et formes libres) */}
                {c.type !== 'plv' && shape !== 'quarter' && !c.free && (
                  <div style={{ position: 'absolute', background: '#e8730f',
                    ...(frontSide === 'bottom' ? { left: 0, right: 0, bottom: 0, height: 4 } : {}),
                    ...(frontSide === 'top' ? { left: 0, right: 0, top: 0, height: 4 } : {}),
                    ...(frontSide === 'left' ? { top: 0, bottom: 0, left: 0, width: 4 } : {}),
                    ...(frontSide === 'right' ? { top: 0, bottom: 0, right: 0, width: 4 } : {}) }} />
                )}
                <span style={{ fontSize: Math.min(16, Math.max(11, px(Math.min(w, h)) / 3)), color: '#111', fontWeight: 700, textAlign: 'center', lineHeight: 1.1, padding: '0 2px', transform: labelTransform(r), whiteSpace: 'normal', textShadow: '0 0 3px rgba(255,255,255,.9), 0 0 3px rgba(255,255,255,.9)', pointerEvents: 'none' }}>
                  {it.txt || c.libelle}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-[11px] mt-2" style={{ color: '#6b665d' }}>
        Largeur = côté horizontal, profondeur = côté vertical · grille 50 cm · magnétisme 5 cm · trait orange = face avant · R : pivoter · Suppr : retirer · flèches : déplacer
      </p>
    </div>
  );
}

export function SelectionBar({ item, catalogue, onChange, onSelect, maxLen }) {
  if (!item) return null;
  const c = catalogue.find((x) => x.k === item.k) || {};
  const isPoint = c.type === 'point';
  const isRepere = c.type === 'repere';
  const isFleche = c.type === 'fleche';
  const noRotate = isPoint || isRepere;
  const rotate = () => onChange((prev) => prev.map((p) => p.id === item.id ? { ...p, r: ((p.r || 0) + (isFleche ? 45 : 90)) % 360 } : p));
  const dup = () => onChange((prev) => { const it = prev.find((p) => p.id === item.id); return [...prev, { ...it, id: 'it_' + Math.random().toString(36).slice(2, 8), x: it.x + 20, y: it.y + 20 }]; });
  const del = () => { onChange((prev) => prev.filter((p) => p.id !== item.id)); onSelect(null); };
  const lenMax = c.maxToVitrine && maxLen ? maxLen : undefined;
  const setW = (v) => { let n = Number(v) || 0; if (lenMax) n = Math.min(n, lenMax); onChange((prev) => prev.map((p) => p.id === item.id ? { ...p, w: n } : p)); };
  const setP = (v) => onChange((prev) => prev.map((p) => p.id === item.id ? { ...p, p: Number(v) || 0 } : p));
  const setTxt = (v) => onChange((prev) => prev.map((p) => p.id === item.id ? { ...p, txt: v } : p));
  const isFree = !!c.free;
  const isCircle = c.shape === 'circle';
  const lenLabel = isFree ? (isCircle ? 'Diamètre' : 'Largeur') : 'Longueur';
  return (
    <div className="mt-3 p-2.5 rounded-lg flex flex-wrap items-center gap-2 text-xs" style={{ background: '#f6f4f0', border: '1px solid #e2ddd4' }} data-testid="croquis-selbar">
      <span className="font-semibold" style={{ color: '#1f1d1a' }}>{c.libelle}</span>
      {c.L && <span style={{ color: '#6b665d' }}>{item.w || c.L}{c.P ? ` × ${c.P}` : ''} cm</span>}
      <span style={{ color: '#6b665d' }}>x {item.x} · y {item.y}</span>
      {!noRotate && <button onClick={rotate} data-testid="croquis-sel-rotate" className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-stone-300 text-stone-700 hover:bg-stone-100"><RotateCw className="w-3 h-3" /> Pivoter</button>}
      {!isPoint && !isRepere && !isFleche && <button onClick={dup} data-testid="croquis-sel-dup" className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-stone-300 text-stone-700 hover:bg-stone-100"><Copy className="w-3 h-3" /> Dupliquer</button>}
      <button onClick={del} data-testid="croquis-sel-del" className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-50 border border-red-200 text-red-700 hover:bg-red-100"><Trash2 className="w-3 h-3" /> Retirer</button>
      {(c.sur_mesure || isFleche) && (
        <label className="inline-flex items-center gap-1" style={{ color: '#6b665d' }}>{lenLabel}
          <input type="number" max={lenMax} value={item.w || c.L || ''} onChange={(e) => setW(e.target.value)} data-testid="croquis-sel-length" className="w-20 px-1.5 py-1 rounded bg-white border border-stone-300 text-stone-800" />
          {lenMax && <span className="text-[10px]" style={{ color: '#6b665d' }}>max {lenMax}</span>}
        </label>
      )}
      {isFree && !isCircle && (
        <label className="inline-flex items-center gap-1" style={{ color: '#6b665d' }}>{c.shape === 'wall' ? 'Épaisseur' : 'Profondeur'}
          <input type="number" value={item.p || c.P || ''} onChange={(e) => setP(e.target.value)} data-testid="croquis-sel-depth" className="w-20 px-1.5 py-1 rounded bg-white border border-stone-300 text-stone-800" />
        </label>
      )}
      {isFree && (
        <input type="text" value={item.txt || ''} onChange={(e) => setTxt(e.target.value)} placeholder="Nom de la forme (ex. Poteau, Mur porteur…)" data-testid="croquis-sel-freelabel" className="flex-1 min-w-[160px] px-2 py-1 rounded bg-white border border-stone-300 text-stone-800" />
      )}
      {isRepere && (
        <input type="text" value={item.txt || ''} onChange={(e) => setTxt(e.target.value)} placeholder="Décrivez le point d'attention" data-testid="croquis-sel-txt" className="flex-1 min-w-[160px] px-2 py-1 rounded bg-white border border-stone-300 text-stone-800" />
      )}
    </div>
  );
}
